import { Component, NgZone, ChangeDetectionStrategy, inject } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { FormValidator } from '@tgx/shared/utils';

type KapaEventOption = 'add' | 'remove';

type KapaApi = {
  open?: (options: { mode?: 'ai' | 'search'; query?: string; submit?: boolean }) => void;
  render?: (options?: { onRender?: () => void }) => void;
  (event: string, handler: (...args: unknown[]) => void, option?: KapaEventOption): void;
};

@Component({
  selector: 'tgx-onboarding-aina-chat',
  templateUrl: './onboarding-aina-chat.component.html',
  styleUrls: ['./onboarding-aina-chat.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class OnboardingAinaChatComponent {
  private readonly ngZone = inject(NgZone);

  // Inline entry point that opens the Kapa (AIna) modal prefilled with the user's question.
  protected readonly form = new FormGroup({
    question: new FormControl('', [
      FormValidator.required('question'),
      FormValidator.minLengthValidator('Please your question', 5),
    ]),
  });

  protected placeholder =
    'To assist in resolving your query efficiently, please provide the following information.' +
    '\n' +
    'Note: Aina, our multilingual support assistant, operates in several languages; however, our support team will respond in English for international teams and in Spanish.' +
    '\n\n' +
    'Be Clear and Specific: Clearly state the reason for your inquiry using full sentences.';

  /**
   * Opens AIna prefilled with the user's question and submits it, keeping the previous conversation
   * intact. It also pins the conversation scroll to the bottom while the answer is prepared (see
   * `pinConversationToBottomUntilAnswer`) and clears the input once the modal is closed.
   */
  protected askAina(): void {
    if (this.form.invalid) {
      this.form.controls.question.markAsDirty();
      return;
    }

    const rawQuestion = this.form.controls.question.value ?? '';
    const question = rawQuestion.trim();
    const kapa = (window as unknown as { Kapa?: KapaApi }).Kapa;
    if (!kapa) {
      return;
    }

    // Clear the question once the modal is closed so the user starts fresh next time (the bot's
    // conversation is kept; only our input field is reset). The Kapa callback runs outside Angular's
    // zone, so run the reset inside it to keep the view in sync.
    const onModalClose = () => {
      kapa('onModalClose', onModalClose, 'remove');
      this.ngZone.run(() => this.form.reset());
    };
    kapa('onModalClose', onModalClose, 'add');

    const openWithQuestion = () => {
      const current = (window as unknown as { Kapa?: KapaApi }).Kapa;
      if (!current) {
        return;
      }
      this.pinConversationToBottomUntilAnswer(current);
      current.open?.({ mode: 'ai', query: question, submit: true });
    };

    if (kapa.open) {
      openWithQuestion();
    } else {
      kapa.render?.({ onRender: openWithQuestion });
    }
  }

  /**
   * Keeps Kapa's conversation scrolled to the bottom from the moment the modal opens until the answer
   * finishes. Kapa renders its UI inside a shadow root on `#kapa-widget-container`, and the
   * conversation scroll area is `.scrollable-container`. Kapa rewrites the scroll position ~1s after
   * reopening without a DOM mutation, so a MutationObserver is not enough; instead we re-pin the
   * scroll on every animation frame, which reliably overrides Kapa's delayed auto-scroll. Pinning
   * stops when the answer completes, when the user scrolls manually, or after a safety timeout.
   */
  private pinConversationToBottomUntilAnswer(kapa: KapaApi): void {
    const shadowRoot = document.querySelector('#kapa-widget-container')?.shadowRoot;
    if (!shadowRoot) {
      return;
    }

    let stopped = false;
    let safetyTimer = 0;
    const releaseEvents: (keyof HTMLElementEventMap)[] = ['wheel', 'touchmove', 'keydown'];

    const stop = () => {
      if (stopped) {
        return;
      }
      stopped = true;
      window.clearTimeout(safetyTimer);
      kapa('onAskAIAnswerCompleted', stop, 'remove');
      releaseEvents.forEach((evt) => shadowRoot.removeEventListener(evt, stop, true));
    };

    const pin = () => {
      if (stopped) {
        return;
      }
      const container = shadowRoot.querySelector<HTMLElement>('.scrollable-container');
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
      requestAnimationFrame(pin);
    };

    // Release the pin as soon as the user tries to scroll, so they can read freely during streaming.
    releaseEvents.forEach((evt) => shadowRoot.addEventListener(evt, stop, true));
    kapa('onAskAIAnswerCompleted', stop, 'add');
    // Safety net in case the answer-completed event never fires (e.g. generation stopped).
    safetyTimer = window.setTimeout(stop, 30000);
    requestAnimationFrame(pin);
  }
}
